"use client";

import { useBranding } from "@/providers/branding-provider";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";

export function useGetUrl() {
  const branding = useBranding();
  const slug = branding.slug;

  return useCallback(
    (path: string) => {
      if (!slug) return path;

      // Ensure path starts with /
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;

      // If path is just "/", return the slug root
      if (normalizedPath === "/") {
        return `/${slug}`;
      }

      return `/${slug}${normalizedPath}`;
    },
    [slug],
  );
}

export function useGetUrlWithQuery() {
  const branding = useBranding();
  const slug = branding.slug;
  const searchParams = useSearchParams();

  return useCallback(
    (path: string, additionalParams?: Record<string, string>) => {
      if (!slug) return path;

      // Ensure path starts with /
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;

      // If path is just "/", return the slug root
      if (normalizedPath === "/") {
        return `/${slug}`;
      }

      const url = `/${slug}${normalizedPath}`;

      // Preserve existing query parameters
      const currentParams = new URLSearchParams(searchParams.toString());

      // Add any additional parameters
      if (additionalParams) {
        Object.entries(additionalParams).forEach(([key, value]) => {
          currentParams.set(key, value);
        });
      }

      const queryString = currentParams.toString();
      return queryString ? `${url}?${queryString}` : url;
    },
    [slug, searchParams],
  );
}

export function useNavigate() {
  const router = useRouter();
  const getUrl = useGetUrl();
  const getUrlWithQuery = useGetUrlWithQuery();

  return {
    push: (path: string) => router.push(getUrl(path)),
    replace: (path: string) => router.replace(getUrl(path)),
    pushWithQuery: (path: string, additionalParams?: Record<string, string>) =>
      router.push(getUrlWithQuery(path, additionalParams)),
    replaceWithQuery: (
      path: string,
      additionalParams?: Record<string, string>,
    ) => router.replace(getUrlWithQuery(path, additionalParams)),
  };
}
